import { restChipPhrase } from '../lib/scheduleAliveness';
import type { ScheduleState } from '../lib/scheduleAliveness';
import type { MemberSpaceDetail } from '../lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// SpaceStateChip — the honest state register beside the space title (T-0200).
//
// Retires the permanent "ACTIVE" chip, which was a soft fake pulse: it painted
// liveness on a room that was honestly resting. What replaces it speaks the
// tense-honest rest register — but ONLY what this surface can prove:
//
//   • archived / revoked  → the real member state, dimmed (unchanged).
//   • active + a ● Schedule between fires → "asleep · wakes 16:40", derived from
//     the schedule the header already reads (the ● timer honestly owns the wake).
//   • active + no ● Schedule → nothing. No rhythm to name, so no chip — silence
//     over a fake pulse.
//
// It never says "awake": a live-running room needs a presence/beat signal, and
// none reaches the browser (the same gap the ◐ law documents). And it never
// derives "asleep" from `room.state`, which the member wire collapses out of
// `sleeping` — only from the ● Schedule, which names the wake honestly.
// ─────────────────────────────────────────────────────────────────────────────

export function SpaceStateChip({
  roomState,
  scheduleState,
}: {
  roomState: MemberSpaceDetail['state'];
  scheduleState: ScheduleState | null;
}) {
  if (roomState === 'archived' || roomState === 'revoked') {
    return (
      <span className="space-header-state" data-state={roomState}>
        {roomState}
      </span>
    );
  }

  const rest = restChipPhrase(scheduleState);
  if (!rest) return null;
  return (
    <span className="space-header-rest" data-state="asleep">
      {rest}
    </span>
  );
}
